/**
 * Utility functions for generating market-specific Shopify redirect URLs
 */

/**
 * List of European Union country codes that use the 'en-eu' market prefix
 */
const EUR_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'CH',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HR',
  'HU',
  'IS',
  'IE',
  'IT',
  'LV',
  'LT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
] as const;

/**
 * Mapping of specific country codes to their market prefixes
 * Note: AU (Australia) is the default market and uses no prefix
 */
const COUNTRY_TO_PREFIX: Record<string, string> = {
  US: 'en-us',
  GB: 'en-gb',
  AE: 'en-ae',
  SG: 'en-sg',
} as const;

/**
 * Base URL for Shopify redirects
 */
const BASE_URL = 'https://tapt.io';

/**
 * Determines the appropriate market prefix based on country code
 *
 * @param countryCode - Two-letter ISO country code (e.g., 'US', 'GB', 'DE')
 * @returns Market prefix with trailing slash if applicable, or empty string for default market (AU and unsupported countries)
 *
 * @example
 * ```typescript
 * marketPrefix('US') // returns 'en-us/'
 * marketPrefix('DE') // returns 'en-eu/'
 * marketPrefix('AU') // returns '' (default market)
 * marketPrefix('JP') // returns '' (unsupported, uses default)
 * ```
 */
export function marketPrefix(countryCode: string): string {
  // AU (Australia) is the default market and uses no prefix
  if (countryCode === 'AU') {
    return '';
  }

  let prefix: string;

  if (EUR_COUNTRIES.includes(countryCode as (typeof EUR_COUNTRIES)[number])) {
    prefix = 'en-eu';
  } else if (COUNTRY_TO_PREFIX[countryCode]) {
    prefix = COUNTRY_TO_PREFIX[countryCode];
  } else {
    prefix = '';
  }

  return prefix === '' ? prefix : `${prefix}/`;
}

/**
 * Generates an encoded redirect URL for Shopify collections based on country code
 *
 * @param countryCode - Two-letter ISO country code (e.g., 'US', 'GB', 'DE')
 * @returns URL-encoded redirect endpoint for the specified market
 *
 * @example
 * ```typescript
 * redirectEndpoint('US') // returns encoded URL for 'https://tapt.io/en-us/collections/all-products'
 * redirectEndpoint('DE') // returns encoded URL for 'https://tapt.io/en-eu/collections/all-products'
 * redirectEndpoint('JP') // returns encoded URL for 'https://tapt.io/collections/all-products'
 * ```
 */
export function redirectEndpoint(countryCode: string): string {
  const market = marketPrefix(countryCode);
  const redirectUrl = `${BASE_URL}/${market}collections/all-products`;
  return encodeURIComponent(redirectUrl);
}

/**
 * Generates the full Shopify redirect URL (unencoded) for display or direct navigation
 *
 * @param countryCode - Two-letter ISO country code (e.g., 'US', 'GB', 'DE')
 * @returns Full redirect URL for the specified market
 *
 * @example
 * ```typescript
 * getRedirectUrl('US') // returns 'https://tapt.io/en-us/collections/all-products'
 * getRedirectUrl('DE') // returns 'https://tapt.io/en-eu/collections/all-products'
 * getRedirectUrl('JP') // returns 'https://tapt.io/collections/all-products'
 * ```
 */
export function getRedirectUrl(countryCode: string): string {
  const market = marketPrefix(countryCode);
  return `${BASE_URL}/${market}collections/all-products`;
}

/**
 * Type definition for supported country codes
 */
export type SupportedCountryCode =
  | keyof typeof COUNTRY_TO_PREFIX
  | (typeof EUR_COUNTRIES)[number]
  | string;

/**
 * Checks if a country code is supported by the Shopify redirect system
 *
 * @param countryCode - Two-letter ISO country code (case-insensitive)
 * @returns True if the country is supported (including AU as default market), false otherwise
 */
export function hasMarketPrefix(countryCode: string): boolean {
  if (!countryCode || typeof countryCode !== 'string') {
    return false;
  }

  const code = countryCode.toUpperCase();

  // AU is the default market and is always supported
  if (code === 'AU') {
    return true;
  }

  return (
    EUR_COUNTRIES.includes(code as (typeof EUR_COUNTRIES)[number]) ||
    COUNTRY_TO_PREFIX.hasOwnProperty(code)
  );
}
